/**
 * Probe spend reconciliation for 重点保障中心 (「探针花费 · 最近 24h」).
 *
 * Two owners, one join key. The gateway owns `usage_logs` (what each probe
 * burned and what it was priced at); the platform owns `balance_transactions`
 * / `revenue_ledger` (what `acc_system` was charged and what the allocator
 * carved into `upstream_funding_batch`). Neither may read the other's tables
 * (ADR-009), so each answers its half over `/internal/*` for the SAME window
 * and ops-api joins them on (sku_id, upstream_id, channel_id) — the three
 * fields every probe charge carries in its metadata as `skuId` /
 * `upstreamId` / `channelId`.
 *
 * Each half returns the exact SQL it ran (window literals inlined) so an
 * operator can run the same query against the database and get the same
 * numbers.
 */

export const PROBE_SPEND_DEFAULT_HOURS = 24;
export const PROBE_SPEND_MAX_HOURS = 168;

/** One (SKU × upstream × channel) group of probe usage rows. Gateway half. */
export interface ProbeSpendLogRow {
  readonly sku_id: string | null;
  readonly sku_name: string | null;
  readonly upstream_id: string;
  readonly upstream_name: string | null;
  readonly channel_id: string | null;
  readonly channel_slug: string | null;
  /** Distinct `usage_logs.model` values, comma-joined. */
  readonly models: string | null;
  readonly shots: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  /** SUM(`cost_usd_micros`): list-price cost as logged, every row. */
  readonly logged_cost_usd_micros: number;
  /** Rows priced with a full procurement snapshot — the ones that get charged. */
  readonly chargeable_shots: number;
  readonly chargeable_cost_usd_micros: number;
  /** Procurement cost (list × SKU cost × account rate) of the chargeable rows. */
  readonly procurement_cost_usd_micros: number;
  /**
   * Post-fix rows (snapshot present) that burned tokens but were not
   * chargeable: no list price, no procurement cost row, or pricing failed.
   */
  readonly unpriced_shots: number;
  /**
   * Rows with no `cost_snapshot_source`: written before probe charging
   * existed. They were never meant to be charged; not an unpriced model.
   */
  readonly pre_fix_shots: number;
}

export interface ProbeSpendLogResponse {
  readonly since: string;
  readonly until: string;
  readonly rows: readonly ProbeSpendLogRow[];
  /** The query that produced `rows`, window literals inlined. */
  readonly sql: string;
}

/** Probe charges on `acc_system` for one join key. Platform half. */
export interface ProbeSpendLedgerRow {
  readonly sku_id: string | null;
  readonly upstream_id: string | null;
  readonly channel_id: string | null;
  readonly charges: number;
  /** What `acc_system` was debited (positive micro-USD). */
  readonly charged_usd_micros: number;
  /** Charges the allocator has carved into at least one funding batch. */
  readonly allocated_charges: number;
  /** SUM(`revenue_ledger.cny_upstream_cost_micros`) over batch slices. */
  readonly allocated_cny_micros: number;
  /**
   * Charges the allocator has settled WITHOUT any funding-batch slice: the
   * upstream had no live batch, so it wrote the cost as overflow
   * (`source_batch_id IS NULL`, cost filled). Settled is terminal for the
   * allocator, so these never become "allocated" by waiting — only a new
   * batch plus the overflow catch-up re-attributes them. The wallet has not
   * moved for them. Absent from an older platform half = 0.
   */
  readonly overflow_charges: number;
}

export interface ProbeSpendLedgerResponse {
  readonly since: string;
  readonly until: string;
  readonly rows: readonly ProbeSpendLedgerRow[];
  readonly sql: string;
}

/**
 * The supplier wallet moves by the allocated slices, so reconciliation walks
 * the whole path by COUNTS (FX-free): chargeable rows → acc_system charges →
 * charges the allocator settled, split into carved-into-a-batch and overflow.
 *
 * In precedence order:
 * `mismatch`: charges ≠ chargeable shots, the amounts differ, or more charges
 * settled than exist.
 * `unbilled`: post-fix rows burned tokens but were never charged (no list
 * price, no procurement snapshot, pricing failed) — spend the wallet should
 * have lost and did not. Decided from the gateway half alone, so it holds
 * even when the ledger is unreadable.
 * `unknown`: the platform half could not be read.
 * `uncharged`: nothing chargeable and nothing charged (pre-fix rows only).
 * `no_funding_batch`: charged correctly, the allocator settled some charges
 * as overflow because the upstream has no funding batch — terminal until a
 * batch is added, the wallet cannot move for them.
 * `pending_allocation`: charged correctly but the allocator (its own cron)
 * has not settled `pending_allocation` of them yet — normal for recent rows.
 * `match`: chargeable shots == charges == allocated charges, and the charged
 * amount equals the chargeable rows' list price.
 */
export type ProbeSpendReconcileStatus =
  | 'match'
  | 'pending_allocation'
  | 'no_funding_batch'
  | 'uncharged'
  | 'unbilled'
  | 'mismatch'
  | 'unknown';

export interface ProbeSpendLeg extends ProbeSpendLogRow {
  readonly charges: number | null;
  readonly charged_usd_micros: number | null;
  readonly allocated_charges: number | null;
  readonly allocated_cny_micros: number | null;
  readonly overflow_charges: number | null;
  readonly status: ProbeSpendReconcileStatus;
  /** Chargeable list price − charged, micro-USD. */
  readonly diff_usd_micros: number | null;
  /** Chargeable shots − charges. */
  readonly diff_charges: number | null;
  /** Charges the allocator has not settled yet (neither batch nor overflow). */
  readonly pending_allocation: number | null;
}

export interface ProbeSpendTotals {
  readonly shots: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly logged_cost_usd_micros: number;
  readonly chargeable_cost_usd_micros: number;
  readonly procurement_cost_usd_micros: number;
  readonly chargeable_shots: number;
  readonly unpriced_shots: number;
  readonly pre_fix_shots: number;
  readonly charges: number | null;
  readonly charged_usd_micros: number | null;
  readonly allocated_charges: number | null;
  readonly allocated_cny_micros: number | null;
  readonly overflow_charges: number | null;
}

export interface GuaranteedProbeSpendResponse {
  readonly hours: number;
  readonly since: string;
  readonly until: string;
  readonly legs: readonly ProbeSpendLeg[];
  readonly totals: ProbeSpendTotals;
  readonly log_sql: string;
  /** Null when the platform half was unavailable. */
  readonly ledger_sql: string | null;
  readonly ledger_available: boolean;
}

/** Normalize the `hours` query value; anything unusable falls back to 24. */
export function probeSpendHours(raw: unknown): number {
  const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= PROBE_SPEND_MAX_HOURS
    ? parsed
    : PROBE_SPEND_DEFAULT_HOURS;
}

/** The ISO window both halves are asked for. `until` is exclusive. */
export function probeSpendWindow(hours: number, nowMs: number): { since: string; until: string } {
  return {
    since: new Date(nowMs - hours * 3_600_000).toISOString(),
    until: new Date(nowMs).toISOString(),
  };
}

/** A canonical ISO-8601 UTC instant (what `toISOString()` produces). */
export function isProbeSpendInstant(value: unknown): value is string {
  return typeof value === 'string'
    && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
    && Number.isFinite(Date.parse(value));
}

/** Inline `$1` / `$2` as the window literals so the SQL runs as shown. */
export function inlineProbeSpendWindow(sql: string, since: string, until: string): string {
  return sql.replaceAll('$1', `'${since}'`).replaceAll('$2', `'${until}'`);
}

function probeSpendKey(skuId: string | null, upstreamId: string | null, channelId: string | null): string {
  return [skuId ?? '', upstreamId ?? '', channelId ?? ''].join('\u0000');
}

export interface ProbeSpendVerdict {
  readonly status: ProbeSpendReconcileStatus;
  readonly diffUsdMicros: number | null;
  readonly diffCharges: number | null;
  readonly pendingAllocation: number | null;
  /** Charges settled as overflow (no funding batch). */
  readonly overflowCharges: number | null;
  /** Post-fix shots with tokens that were never charged. */
  readonly unbilledShots: number;
}

/** The reconcile verdict for one leg (or a sum of legs). */
export function reconcileProbeSpend(params: {
  chargeableShots: number;
  chargeableUsdMicros: number;
  unpricedShots: number;
  charges: number | null;
  chargedUsdMicros: number | null;
  allocatedCharges: number | null;
  overflowCharges: number | null;
}): ProbeSpendVerdict {
  const { chargeableShots, chargeableUsdMicros, unpricedShots, charges, chargedUsdMicros, allocatedCharges } = params;
  const unbilledShots = unpricedShots;
  if (charges === null || chargedUsdMicros === null || allocatedCharges === null) {
    return {
      status: unbilledShots > 0 ? 'unbilled' : 'unknown',
      diffUsdMicros: null,
      diffCharges: null,
      pendingAllocation: null,
      overflowCharges: null,
      unbilledShots,
    };
  }
  const overflowCharges = params.overflowCharges ?? 0;
  const diffUsdMicros = chargeableUsdMicros - chargedUsdMicros;
  const diffCharges = chargeableShots - charges;
  const pendingAllocation = Math.max(0, charges - allocatedCharges - overflowCharges);
  const base = { diffUsdMicros, diffCharges, pendingAllocation, overflowCharges, unbilledShots };
  if (diffCharges !== 0 || diffUsdMicros !== 0 || allocatedCharges + overflowCharges > charges) {
    return { status: 'mismatch', ...base };
  }
  if (unbilledShots > 0) return { status: 'unbilled', ...base };
  if (chargeableShots === 0 && charges === 0) return { status: 'uncharged', ...base };
  if (overflowCharges > 0) return { status: 'no_funding_batch', ...base };
  if (pendingAllocation > 0) return { status: 'pending_allocation', ...base };
  return { status: 'match', ...base };
}

type UnjudgedLeg = Omit<ProbeSpendLeg, 'status' | 'diff_usd_micros' | 'diff_charges' | 'pending_allocation'>;

function withVerdict(leg: UnjudgedLeg): ProbeSpendLeg {
  const verdict = reconcileProbeSpend({
    chargeableShots: leg.chargeable_shots,
    chargeableUsdMicros: leg.chargeable_cost_usd_micros,
    unpricedShots: leg.unpriced_shots,
    charges: leg.charges,
    chargedUsdMicros: leg.charged_usd_micros,
    allocatedCharges: leg.allocated_charges,
    overflowCharges: leg.overflow_charges,
  });
  return {
    ...leg,
    status: verdict.status,
    diff_usd_micros: verdict.diffUsdMicros,
    diff_charges: verdict.diffCharges,
    pending_allocation: verdict.pendingAllocation,
  };
}

/**
 * Join the two halves. Ledger groups with no usage rows (a charge whose row
 * insert failed) still appear, as legs with zero logged spend, so a
 * mismatch is never hidden by the join.
 */
export function mergeProbeSpend(params: {
  hours: number;
  log: ProbeSpendLogResponse;
  ledger: ProbeSpendLedgerResponse | null;
}): GuaranteedProbeSpendResponse {
  const { log, ledger } = params;
  const ledgerByKey = new Map<string, ProbeSpendLedgerRow>();
  for (const row of ledger?.rows ?? []) {
    ledgerByKey.set(probeSpendKey(row.sku_id, row.upstream_id, row.channel_id), row);
  }
  const legs: ProbeSpendLeg[] = [];
  const seen = new Set<string>();
  for (const row of log.rows) {
    const key = probeSpendKey(row.sku_id, row.upstream_id, row.channel_id);
    seen.add(key);
    const charged = ledger === null ? null : ledgerByKey.get(key) ?? null;
    const ledgerSide = {
      charges: ledger === null ? null : charged?.charges ?? 0,
      charged_usd_micros: ledger === null ? null : charged?.charged_usd_micros ?? 0,
      allocated_charges: ledger === null ? null : charged?.allocated_charges ?? 0,
      allocated_cny_micros: ledger === null ? null : charged?.allocated_cny_micros ?? 0,
      overflow_charges: ledger === null ? null : charged?.overflow_charges ?? 0,
    };
    legs.push(withVerdict({ ...row, ...ledgerSide }));
  }
  for (const row of ledger?.rows ?? []) {
    const key = probeSpendKey(row.sku_id, row.upstream_id, row.channel_id);
    if (seen.has(key)) continue;
    legs.push(withVerdict({
      sku_id: row.sku_id,
      sku_name: null,
      upstream_id: row.upstream_id ?? '',
      upstream_name: null,
      channel_id: row.channel_id,
      channel_slug: null,
      models: null,
      shots: 0,
      input_tokens: 0,
      output_tokens: 0,
      logged_cost_usd_micros: 0,
      chargeable_shots: 0,
      chargeable_cost_usd_micros: 0,
      procurement_cost_usd_micros: 0,
      unpriced_shots: 0,
      pre_fix_shots: 0,
      charges: row.charges,
      charged_usd_micros: row.charged_usd_micros,
      allocated_charges: row.allocated_charges,
      allocated_cny_micros: row.allocated_cny_micros,
      overflow_charges: row.overflow_charges,
    }));
  }
  const sum = (pick: (leg: ProbeSpendLeg) => number): number =>
    legs.reduce((total, leg) => total + pick(leg), 0);
  return {
    hours: params.hours,
    since: log.since,
    until: log.until,
    legs,
    totals: {
      shots: sum((leg) => leg.shots),
      input_tokens: sum((leg) => leg.input_tokens),
      output_tokens: sum((leg) => leg.output_tokens),
      logged_cost_usd_micros: sum((leg) => leg.logged_cost_usd_micros),
      chargeable_cost_usd_micros: sum((leg) => leg.chargeable_cost_usd_micros),
      procurement_cost_usd_micros: sum((leg) => leg.procurement_cost_usd_micros),
      chargeable_shots: sum((leg) => leg.chargeable_shots),
      unpriced_shots: sum((leg) => leg.unpriced_shots),
      pre_fix_shots: sum((leg) => leg.pre_fix_shots),
      charges: ledger === null ? null : sum((leg) => leg.charges ?? 0),
      charged_usd_micros: ledger === null ? null : sum((leg) => leg.charged_usd_micros ?? 0),
      allocated_charges: ledger === null ? null : sum((leg) => leg.allocated_charges ?? 0),
      allocated_cny_micros: ledger === null ? null : sum((leg) => leg.allocated_cny_micros ?? 0),
      overflow_charges: ledger === null ? null : sum((leg) => leg.overflow_charges ?? 0),
    },
    log_sql: log.sql,
    ledger_sql: ledger?.sql ?? null,
    ledger_available: ledger !== null,
  };
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value))
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseLogRow(raw: unknown): ProbeSpendLogRow | null {
  const row = record(raw);
  if (row === null) return null;
  const upstreamId = text(row.upstream_id);
  const numbers = [
    'shots', 'input_tokens', 'output_tokens', 'logged_cost_usd_micros', 'chargeable_shots',
    'chargeable_cost_usd_micros', 'procurement_cost_usd_micros', 'unpriced_shots', 'pre_fix_shots',
  ].map((key) => count(row[key]));
  const [shots, input, output, logged, chargeableShots, chargeable, procurement, unpriced, preFix] = numbers;
  if (
    upstreamId === null || shots == null || input == null || output == null || logged == null
    || chargeableShots == null || chargeable == null || procurement == null || unpriced == null || preFix == null
  ) {
    return null;
  }
  return {
    sku_id: text(row.sku_id),
    sku_name: text(row.sku_name),
    upstream_id: upstreamId,
    upstream_name: text(row.upstream_name),
    channel_id: text(row.channel_id),
    channel_slug: text(row.channel_slug),
    models: text(row.models),
    shots,
    input_tokens: input,
    output_tokens: output,
    logged_cost_usd_micros: logged,
    chargeable_shots: chargeableShots,
    chargeable_cost_usd_micros: chargeable,
    procurement_cost_usd_micros: procurement,
    unpriced_shots: unpriced,
    pre_fix_shots: preFix,
  };
}

function parseLedgerRow(raw: unknown): ProbeSpendLedgerRow | null {
  const row = record(raw);
  if (row === null) return null;
  const charges = count(row.charges);
  const charged = count(row.charged_usd_micros);
  const allocated = count(row.allocated_charges);
  const allocatedCny = count(row.allocated_cny_micros);
  // Additive field: a platform half that predates it reports no overflow.
  const overflow = row.overflow_charges === undefined ? 0 : count(row.overflow_charges);
  if (charges === null || charged === null || allocated === null || allocatedCny === null || overflow === null) {
    return null;
  }
  return {
    sku_id: text(row.sku_id),
    upstream_id: text(row.upstream_id),
    channel_id: text(row.channel_id),
    charges,
    charged_usd_micros: charged,
    allocated_charges: allocated,
    allocated_cny_micros: allocatedCny,
    overflow_charges: overflow,
  };
}

function parseWindowed<T>(
  raw: unknown,
  parseRow: (row: unknown) => T | null,
): { since: string; until: string; rows: T[]; sql: string } | null {
  const body = record(raw);
  if (body === null || !Array.isArray(body.rows)) return null;
  const since = text(body.since);
  const until = text(body.until);
  const sql = text(body.sql);
  if (since === null || until === null || sql === null) return null;
  const rows: T[] = [];
  for (const item of body.rows) {
    const parsed = parseRow(item);
    if (parsed === null) return null;
    rows.push(parsed);
  }
  return { since, until, rows, sql };
}

export function parseProbeSpendLogResponse(raw: unknown): ProbeSpendLogResponse | null {
  return parseWindowed(raw, parseLogRow);
}

export function parseProbeSpendLedgerResponse(raw: unknown): ProbeSpendLedgerResponse | null {
  return parseWindowed(raw, parseLedgerRow);
}
